import React, { useState, useEffect } from 'react';
import { categoryAPI } from '../services/api';
import { PageLayout, ConfirmDialog } from './ui';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [uncategorized, setUncategorized] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
  
  // New category form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', icon: '📁' });
  
  // Edit category
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Drag and drop state
  const [draggedApp, setDraggedApp] = useState(null);

  const commonIcons = ['📁', '🎧', '💼', '🔖', '📊', '🛠️', '📧', '💰', '🔒', '📱', '🖥️', '☁️', '📝', '🎯', '👥'];

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      // Use admin endpoint to see ALL apps for management
      const data = await categoryAPI.getCategoriesAdmin();
      setCategories(data.categories || []);
      setUncategorized(data.uncategorized || []);
    } catch (err) {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await categoryAPI.createCategory(newCategory);
      setSuccess('Category created successfully');
      setShowNewForm(false);
      setNewCategory({ name: '', description: '', icon: '📁' });
      loadCategories();
    } catch (err) {
      setError(err.message || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      await categoryAPI.updateCategory(editingCategory.id, editingCategory);
      setSuccess('Category updated successfully');
      setEditingCategory(null);
      loadCategories();
    } catch (err) {
      setError(err.message || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Category',
      message: 'Delete this category? Applications will become uncategorized.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await categoryAPI.deleteCategory(categoryId);
          setSuccess('Category deleted successfully');
          loadCategories();
        } catch (err) {
          setError(err.message || 'Failed to delete category');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
        }
      }
    });
  };

  const handleDragStart = (e, app) => {
    setDraggedApp(app);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, categoryId) => {
    e.preventDefault();
    if (!draggedApp) return;
    
    try {
      await categoryAPI.assignAppToCategory(categoryId, draggedApp.id);
      setSuccess(`Moved "${draggedApp.label}" to category`);
      loadCategories();
    } catch (err) {
      setError('Failed to move application');
    }
    setDraggedApp(null);
  };

  const clearMessages = () => {
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 3000);
  };

  useEffect(() => {
    if (error || success) clearMessages();
  }, [error, success]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Category Management"
      backTo="/management"
      backLabel="Back to Management"
      actions={
        <button
          onClick={() => setShowNewForm(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover text-sm font-medium"
        >
          + New Category
        </button>
      }
    >
      <div className="max-w-7xl mx-auto">

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Instructions */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-primary">
          <strong>💡 Tip:</strong> Drag and drop applications between categories to organize them. 
          Applications in each category will appear in this order on the user dashboard.
        </p>
      </div>

      {/* New Category Form */}
      {showNewForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Create New Category</h3>
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {commonIcons.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setNewCategory({...newCategory, icon})}
                      className={`text-2xl p-2 rounded ${newCategory.icon === icon ? 'bg-primary-light ring-2 ring-primary' : 'hover:bg-gray-100'}`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                  placeholder="e.g., Support"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Optional description"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-hover">
                Create Category
              </button>
              <button type="button" onClick={() => setShowNewForm(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories */}
      <div className="space-y-6">
        {categories.map(category => (
          <div
            key={category.id}
            className="bg-white rounded-lg shadow border"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, category.id)}
          >
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              {editingCategory?.id === category.id ? (
                <form onSubmit={handleUpdateCategory} className="flex-1 flex gap-2 items-center">
                  <div className="flex gap-1">
                    {commonIcons.slice(0, 8).map(icon => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setEditingCategory({...editingCategory, icon})}
                        className={`text-lg p-1 rounded ${editingCategory.icon === icon ? 'bg-primary-light' : ''}`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                    className="px-2 py-1 border rounded"
                  />
                  <button type="submit" className="text-green-600 hover:text-green-800">✓</button>
                  <button type="button" onClick={() => setEditingCategory(null)} className="text-red-600 hover:text-red-800">✕</button>
                </form>
              ) : (
                <>
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <span className="text-2xl">{category.icon}</span>
                    {category.name}
                    <span className="text-sm text-gray-500 font-normal">
                      ({category.applications?.length || 0} apps)
                    </span>
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingCategory(category)}
                      className="text-primary hover:text-indigo-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="p-4">
              {category.applications?.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {category.applications.map(app => (
                    <div
                      key={app.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, app)}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg cursor-move hover:bg-gray-200 transition-colors"
                    >
                      {app.logo_url ? (
                        <img src={app.logo_url.startsWith('/') ? `${process.env.REACT_APP_API_URL || ''}${app.logo_url}` : app.logo_url} alt="" className="w-6 h-6 rounded object-contain" onError={(e) => { e.target.style.display = 'none'; }}/>
                      ) : (
                        <div className="w-6 h-6 bg-primary-light rounded flex items-center justify-center text-primary text-xs font-bold">
                          {app.label?.charAt(0) || '?'}
                        </div>
                      )}
                      <span className="text-sm font-medium">{app.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4 border-2 border-dashed rounded-lg">
                  Drag applications here
                </p>
              )}
            </div>
          </div>
        ))}

        {/* Uncategorized */}
        <div
          className="bg-white rounded-lg shadow border"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, 0)}
        >
          <div className="p-4 border-b bg-gray-100">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-2xl">📋</span>
              Uncategorized
              <span className="text-sm text-gray-500 font-normal">
                ({uncategorized.length} apps)
              </span>
            </h2>
          </div>
          <div className="p-4">
            {uncategorized.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {uncategorized.map(app => (
                  <div
                    key={app.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, app)}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg cursor-move hover:bg-gray-200 transition-colors"
                  >
                    {app.logo_url ? (
                      <img src={app.logo_url.startsWith('/') ? `${process.env.REACT_APP_API_URL || ''}${app.logo_url}` : app.logo_url} alt="" className="w-6 h-6 rounded object-contain" onError={(e) => { e.target.style.display = 'none'; }}/>
                    ) : (
                      <div className="w-6 h-6 bg-primary-light rounded flex items-center justify-center text-primary text-xs font-bold">
                        {app.label?.charAt(0) || '?'}
                      </div>
                    )}
                    <span className="text-sm font-medium">{app.label}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">
                All applications are categorized!
              </p>
            )}
          </div>
        </div>
      </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        variant={confirmDialog.variant}
      />
    </PageLayout>
  );
};

export default CategoryManagement;
